/**
 * Browser-side anon + session identity for analytics.
 *
 * Two values, both stable UUIDs generated client-side:
 *   - rb_anon_id: 365-day first-party cookie + localStorage mirror.
 *     Stitches pre-signup → post-signup behavior in user_interactions.
 *   - rb_session_id: sessionStorage, rotated after >30 min of inactivity.
 *     Used for events/session aggregations.
 *
 * Both are sent to the server in two ways depending on transport:
 *   - sendBeacon JSON payload (no custom headers possible).
 *   - X-Robutler-Anon / X-Robutler-Session request headers on regular fetch().
 */

const ANON_COOKIE = 'rb_anon_id';
const ANON_LS = 'rb_anon_id';
const ANON_DAYS = 365;

const SESSION_KEY = 'rb_session_id';
const SESSION_LAST_ACTIVE_KEY = 'rb_session_last';
const SESSION_IDLE_MS = 30 * 60 * 1000; // 30 min

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function uuid(): string {
  // crypto.randomUUID is available in all evergreen browsers + Node 18+.
  // Fall back to a tiny v4 generator for ancient Safari that still saw production.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function readCookie(name: string): string | null {
  if (!isBrowser()) return null;
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]+)`));
  return m ? decodeURIComponent(m[1]) : null;
}

function writeCookie(name: string, value: string, days: number): void {
  if (!isBrowser()) return;
  const expires = new Date(Date.now() + days * 86_400_000).toUTCString();
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Expires=${expires}; Path=/; SameSite=Lax${secure}`;
}

/**
 * Get or create the persistent anon id. Ensures the cookie + localStorage
 * mirror agree (cookie wins when both present; localStorage seeds after
 * a cookie wipe so anonymous history survives).
 */
export function getOrCreateAnonId(): string | null {
  if (!isBrowser()) return null;
  let anon = readCookie(ANON_COOKIE);
  let lsValue: string | null = null;
  try {
    lsValue = localStorage.getItem(ANON_LS);
  } catch {
    /* private browsing: localStorage unavailable */
  }
  if (!anon && lsValue) anon = lsValue;
  if (!anon) anon = uuid();
  writeCookie(ANON_COOKIE, anon, ANON_DAYS);
  try {
    if (lsValue !== anon) localStorage.setItem(ANON_LS, anon);
  } catch {
    /* ignore */
  }
  return anon;
}

/**
 * Get or rotate the per-tab session id. Rotates if the last activity
 * timestamp is older than SESSION_IDLE_MS. Every successful read bumps
 * the activity timestamp.
 */
export function getOrCreateSessionId(): string | null {
  if (!isBrowser()) return null;
  let sid: string | null = null;
  let last = 0;
  try {
    sid = sessionStorage.getItem(SESSION_KEY);
    const raw = sessionStorage.getItem(SESSION_LAST_ACTIVE_KEY);
    last = raw ? Number(raw) : 0;
  } catch {
    return null;
  }
  const now = Date.now();
  if (!sid || !Number.isFinite(last) || now - last > SESSION_IDLE_MS) {
    sid = uuid();
  }
  try {
    sessionStorage.setItem(SESSION_KEY, sid);
    sessionStorage.setItem(SESSION_LAST_ACTIVE_KEY, String(now));
  } catch {
    /* ignore */
  }
  return sid;
}

/**
 * Patch the global fetch wrapper to inject X-Robutler-Anon /
 * X-Robutler-Session headers on every same-origin request. sendBeacon
 * cannot carry headers — beacon callsites inject anonId / sessionId
 * into the JSON payload instead.
 *
 * Idempotent: subsequent calls are no-ops.
 */
export function installFetchIdentityHeaders(): void {
  if (!isBrowser()) return;
  const w = window as Window & { __rbFetchPatched?: boolean };
  if (w.__rbFetchPatched) return;
  w.__rbFetchPatched = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = function patchedFetch(input, init) {
    try {
      let url: string;
      if (typeof input === 'string') url = input;
      else if (input instanceof URL) url = input.toString();
      else url = input.url;

      const isSameOrigin = url.startsWith('/') || url.startsWith(window.location.origin);
      if (!isSameOrigin) return originalFetch(input, init);

      const headers = new Headers(init?.headers || (input instanceof Request ? input.headers : undefined));
      if (!headers.has('x-robutler-anon')) {
        const anon = getOrCreateAnonId();
        if (anon) headers.set('x-robutler-anon', anon);
      }
      if (!headers.has('x-robutler-session')) {
        const sid = getOrCreateSessionId();
        if (sid) headers.set('x-robutler-session', sid);
      }
      return originalFetch(input, { ...init, headers });
    } catch {
      // Defensive: never let a tracking shim break a real fetch.
      return originalFetch(input, init);
    }
  };
}
